import sanitizeHtml from 'sanitize-html';

import type { Item } from '@/api/gen/_model/item';
import type { MatchPairRef } from '@/api/gen/_model/matchPairRef';
import type { MatchPairSummary } from '@/api/gen/_model/matchPairSummary';

/**
 * MATCH(짝맞추기) pair 를 사용자 화면에 노출할 때 쓰는 단일 표시 규칙.
 *
 * PRD 5.7: Build 를 제외한 모든 사용자 노출 화면은 동일한 카드 상태/시각 규칙을 공유한다.
 * - 좌/우 라벨은 `좌측 → 우측` 형태로 표기한다(구분자는 이 모듈의 상수만 사용).
 * - 라벨은 text → richText 평문 → equation → 이미지 placeholder 순으로 해석하며, 내부 식별자(id)는
 *   절대 노출하지 않는다(PRD 5.7 위반 방지).
 * - 정오 판정은 공개된 leftId/rightId(안정적 식별자)로만 한다. 라벨은 표시 전용이다.
 */
export const MATCH_PAIR_SEPARATOR = '→';

/** 사용자 노출용 placeholder. 라벨을 해석할 수 없을 때 raw id 대신 사용한다. */
export interface MatchLabelPlaceholders {
  /** 이미지 항목이라 텍스트 라벨이 없을 때 */
  imageLabel: string;
  /** 텍스트/이미지 어느 것으로도 해석되지 않을 때 */
  fallbackLabel: string;
}

/**
 * 라벨을 채우지 않고 빈 값으로 두고 싶은 pure/비-UI 컨텍스트용 placeholder.
 * (예: Worksheet 블록 빌더, play 결과 변환기 — 훅을 쓸 수 없는 곳)
 * 빈 라벨은 호출부에서 필터링되거나 그대로 표기된다. 어떤 경우에도 raw id 는 노출하지 않는다.
 */
export const EMPTY_MATCH_PLACEHOLDERS: MatchLabelPlaceholders = {
  imageLabel: '',
  fallbackLabel: '',
};

const LATEX_NODE_PATTERN =
  /<(?:span|math-inline)\b[^>]*\bdata-latex=(["'])(.*?)\1[^>]*(?:>[\s\S]*?<\/(?:span|math-inline)>|\/?>)/gi;
const BLOCK_SEPARATOR_PATTERN =
  /<br\s*\/?>|<\/?(?:p|div|li|ul|ol|blockquote|h[1-6])\b[^>]*>/gi;

/**
 * richText(HTML) 를 평문으로 변환한다. SSR/CSR 모두에서 동작하도록 DOM 대신
 * sanitize-html 을 사용한다(toSeoPlainText 와 동일한 latex 추출 규칙).
 */
export const richTextToPlainText = (value?: string): string => {
  if (!value) return '';

  return sanitizeHtml(
    value
      .replace(LATEX_NODE_PATTERN, (_match, _quote, latex: string) => latex)
      .replace(BLOCK_SEPARATOR_PATTERN, ' '),
    { allowedTags: [], allowedAttributes: {} },
  )
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * MATCH 항목의 중복 검사와 편집 초기값에 사용하는 canonical 텍스트.
 * 이미지 전용 항목은 사용자에게 보이는 placeholder가 중복으로 오인되지 않도록 빈 값이다.
 */
export const getMatchItemVisibleText = (
  item:
    | {
        text?: string;
        richText?: string;
        equation?: string;
        imageUrl?: string;
      }
    | undefined,
): string => {
  const text = item?.text?.trim();
  if (text) return text;

  const richText = richTextToPlainText(item?.richText);
  if (richText) return richText;

  return item?.equation?.trim() ?? '';
};
/**
 * 항목 1개의 표시 라벨을 해석한다.
 * 순서: text → richText 평문 → equation → 이미지 placeholder → fallback placeholder.
 * 어떤 경우에도 raw id 를 반환하지 않는다.
 */
export const resolveItemLabel = (
  item: Item | undefined,
  placeholders: MatchLabelPlaceholders,
): string => {
  const visibleText = getMatchItemVisibleText(item);
  if (visibleText) return visibleText;

  if (item?.imageUrl) return placeholders.imageLabel;

  return placeholders.fallbackLabel;
};

/** id → Item 조회 맵. */
export const indexItemsById = (items: readonly Item[] | undefined) =>
  new Map((items ?? []).map(item => [item.id, item]));

/**
 * pair 목록(MatchPairRef[])을 items 로 해석해 사용자 표시용 summary 로 만든다.
 * 서버가 summary 를 직접 내려준 경우 그것을 우선 사용하고, 이 함수는 폴백 경로다.
 */
export const buildPairSummaries = (
  pairs: readonly MatchPairRef[] | undefined,
  {
    leftItems,
    rightItems,
    placeholders,
  }: {
    leftItems?: readonly Item[];
    rightItems?: readonly Item[];
    placeholders: MatchLabelPlaceholders;
  },
): MatchPairSummary[] => {
  if (!pairs?.length) return [];

  const leftById = indexItemsById(leftItems);
  const rightById = indexItemsById(rightItems);

  return pairs.map(pair => {
    const leftLabel = resolveItemLabel(leftById.get(pair.leftId), placeholders);
    const rightLabel = resolveItemLabel(
      rightById.get(pair.rightId),
      placeholders,
    );

    return {
      leftLabel,
      rightLabel,
      summary: formatPairSummary(leftLabel, rightLabel),
    };
  });
};

/** `좌측 → 우측` 표기. */
export const formatPairSummary = (leftLabel: string, rightLabel: string) =>
  `${leftLabel} ${MATCH_PAIR_SEPARATOR} ${rightLabel}`;

/**
 * 서버 summary 를 우선 사용하고, 없으면 pair+items 로 폴백 해석한다.
 * 모든 표시 surface 가 동일한 우선순위를 공유하도록 한 곳에 모은다.
 */
export const resolvePairSummaries = ({
  summaries,
  pairs,
  leftItems,
  rightItems,
  placeholders,
}: {
  summaries?: readonly MatchPairSummary[];
  pairs?: readonly MatchPairRef[];
  leftItems?: readonly Item[];
  rightItems?: readonly Item[];
  placeholders: MatchLabelPlaceholders;
}): MatchPairSummary[] => {
  if (summaries?.length) {
    return summaries.map(summary => ({
      ...summary,
      summary:
        summary.summary ||
        formatPairSummary(summary.leftLabel, summary.rightLabel),
    }));
  }

  return buildPairSummaries(pairs, { leftItems, rightItems, placeholders });
};

/** 정답 pair 를 leftId → rightId 로 색인한다(정오 판정의 진실 소스). */
export const indexCorrectRightIdByLeftId = (
  correctPairs: readonly MatchPairRef[] | undefined,
) => new Map((correctPairs ?? []).map(pair => [pair.leftId, pair.rightId]));

export type MatchPairCorrectness = 'correct' | 'wrong' | 'unknown';

/**
 * 제출 pair 하나의 정오를 공개된 id 기준으로 판정한다.
 * 정답 공개 또는 제출 id가 없으면 표시 라벨과 무관하게 unknown이다.
 */
export const getSubmittedPairCorrectnessById = (
  submittedPair: MatchPairRef | undefined,
  correctRightIdByLeftId: Map<string, string>,
): MatchPairCorrectness => {
  if (!submittedPair || correctRightIdByLeftId.size === 0) return 'unknown';

  const correctRightId = correctRightIdByLeftId.get(submittedPair.leftId);
  if (correctRightId === undefined) return 'unknown';

  return correctRightId === submittedPair.rightId ? 'correct' : 'wrong';
};

export interface MatchPairRow {
  summary: MatchPairSummary;
  /** 공개된 id로 판정한 제출 pair 상태. */
  correctness: MatchPairCorrectness;
}

/**
 * 제출한 pair 목록에 공개된 id 기준 상태를 부여한다.
 * summary 라벨은 중복될 수 있으므로 정오 판정에 절대 사용하지 않는다.
 */
export const markSubmittedPairWrongness = ({
  submittedSummaries,
  submittedPairs,
  correctPairs,
}: {
  submittedSummaries: readonly MatchPairSummary[];
  submittedPairs?: readonly MatchPairRef[];
  correctPairs?: readonly MatchPairRef[];
}): MatchPairRow[] => {
  const correctRightIdByLeftId = indexCorrectRightIdByLeftId(correctPairs);

  return submittedSummaries.map((summary, index) => {
    const correctness = getSubmittedPairCorrectnessById(
      submittedPairs?.[index],
      correctRightIdByLeftId,
    );
    return { summary, correctness };
  });
};
/**
 * Revealed answer rows are always canonical correct pairs. Submitted rows stay
 * in the submitted-answer area so a wrong submitted pair is never duplicated
 * in the revealed answer list.
 */
export const getRevealedCorrectPairRows = ({
  correctPairs,
  correctSummaries,
}: {
  correctPairs?: readonly MatchPairRef[];
  correctSummaries: readonly MatchPairSummary[];
}): MatchPairRow[] => {
  if (!correctPairs?.length) {
    return correctSummaries.map(summary => ({
      summary,
      correctness: 'correct',
    }));
  }

  return correctPairs.flatMap((_, index) => {
    const summary = correctSummaries[index];
    return summary ? [{ summary, correctness: 'correct' as const }] : [];
  });
};
/**
 * MATCH 질문 전체의 정오 상태를 공개된 pair id만으로 판정한다.
 * 미제출, 미공개, 또는 식별할 수 없는 pair는 unknown이다.
 */
export const getMatchSubmissionCorrectness = ({
  submittedPairs,
  correctPairs,
}: {
  submittedPairs?: readonly MatchPairRef[];
  correctPairs?: readonly MatchPairRef[];
}): MatchPairCorrectness => {
  if (!submittedPairs?.length || !correctPairs?.length) return 'unknown';

  const correctRightIdByLeftId = indexCorrectRightIdByLeftId(correctPairs);
  const pairCorrectnesses = submittedPairs.map(pair =>
    getSubmittedPairCorrectnessById(pair, correctRightIdByLeftId),
  );

  if (pairCorrectnesses.includes('wrong')) return 'wrong';
  if (pairCorrectnesses.includes('unknown')) return 'unknown';

  const submittedRightIdByLeftId = new Map(
    submittedPairs.map(pair => [pair.leftId, pair.rightId]),
  );
  if (
    submittedRightIdByLeftId.size !== submittedPairs.length ||
    submittedPairs.length !== correctPairs.length
  ) {
    return 'wrong';
  }

  return correctPairs.every(
    pair => submittedRightIdByLeftId.get(pair.leftId) === pair.rightId,
  )
    ? 'correct'
    : 'wrong';
};
